import type { User } from "src/api/types/user";
import config from "src/apiServices/appConfig";
import axios from "axios";
const s=config.BASE_URL+config.API_VERSION;

type SignInRequest = {
  email?: string;
  client_secret?: string;
  client_id?:string
};



type AuthReponse = {
  status: boolean;
  code: number;
  message: string;
  clientDetails?: any;
};

type SignUpRequest = {
  email: string;
  name: string;
  client_secret: string;
};

type SignUpResponse = {
  status: boolean;
  code: number;
  message: string;
  data?: any;
  token?: {
    access: { token: string; expires: string };
    refresh: { token: string; expires: string };
  };
};

class AuthApi {
  private ACCESS_TOKEN_KEY = "access_token";
  private CURRENT_USER_KEY = "current_user";
  
  // Helper to remove client_secret from clientDetails
  private sanitizeAuthResponse(response: any) {
    if (response && response.clientDetails) {
      const sanitized = { ...response, clientDetails: { ...response.clientDetails } };
      delete sanitized.clientDetails.client_secret;
      return sanitized;
    }
    return response;
  }

  async signIn(request: SignInRequest): Promise<AuthReponse & { user?: User }> {
    try {
      const response = await axios.post<
        AuthReponse & {
          access_token?: string;
          refresh_token?: string;
        }
      >(`${s}/auth/login`, request, {
        withCredentials: false,
      });


   
 
   
      if (response.data.status) {
        if (response.data.access_token) {
          localStorage.setItem(
            this.ACCESS_TOKEN_KEY,
            response.data.access_token
          );
        }

        if (response.data.refresh_token) {
          localStorage.setItem("refresh_token", response.data.refresh_token);
        }

        // Save the full auth response (without client_secret) in localStorage
        const sanitized = this.sanitizeAuthResponse(response.data);
        localStorage.setItem('auth_response', JSON.stringify(sanitized));
        
        
        const user = {
          id: request.email,
          email: request.email,
          client_id: response.data.clientDetails?.client_id || request.client_id
        
        } as User;

        localStorage.setItem(this.CURRENT_USER_KEY, JSON.stringify(user));
        return { ...response.data, user };
      }

      return response.data;
    } catch (error: any) {
      console.error("Login Failed: ", error);
      throw error.response?.data || { message: "Invalid email or password" };
    }
  }

  // Return the stored access token
  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  // Clear tokens from localStorage
  private clearToken(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem("refresh_token");
    localStorage.removeItem(this.CURRENT_USER_KEY);
  }

  // Update logout method to clear local storage
  async logout(): Promise<void> {
    try {
        this.clearToken(); 
      await axios.post(
        `${s}/logout`,
        {},
        {
          withCredentials: true,
        }
      );
    // Clear the token from localStorage
    } catch (error) {
      console.error("Logout failed", error);
    }
  }

  async signUp(request: SignUpRequest): Promise<SignUpResponse> {
    try {
      const response = await axios.post<AuthReponse>(
        `${s}/auth/register`,
        request,
        {
          withCredentials: true,
        }
      );

      // if(response.data.status && response.data.tokens){
      //   this.storeTokens(response.data.tokens)
      // }

      return response.data;
    } catch (error: any) {
      console.error("Registration failed: ", error);
      throw (
        error.response?.data || {
          message: "Registration failed. Please try again.",
        }
      );
    }
  }

  async refreshToken(): Promise<void> {
    try {
      await axios.post(
        `${s}/refreshTokens`,
        {},
        {
          withCredentials: true,
        }
      );
    } catch (error) {
      console.error("Token refresh failed", error);
    }
  }

  async changePassword(
    oldPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<void> {
    try {
      await axios.post(
        `${s}/changePassword`,
        {
          old_password: oldPassword,
          password: newPassword,
          confirm_password: confirmPassword,
        },
        { withCredentials: true }
      );
    } catch (error) {
      console.error("Password change failed:", error);
      throw error.response?.data || { message: "Failed to change password" };
    }
  }

  async me(): Promise<User> {
    try {
      // Check if we have a token
      const token = this.getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Check if we have cached user data
      const userStr = localStorage.getItem(this.CURRENT_USER_KEY);
      if (userStr) {
        return JSON.parse(userStr);
      }

      // If we have a token but no user data, try to fetch user data
      const response = await axios.get(`${s}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.status && response.data.data) {
        const user = response.data.data;
        // Ensure client_id is preserved if it exists in the response
        if (response.data.data.client_id) {
          user.client_id = response.data.data.client_id;
        }
        localStorage.setItem(this.CURRENT_USER_KEY, JSON.stringify(user));
        return user;
      }

      throw new Error("Failed to get user data");
    } catch (error) {
      // Clear any stale data
      this.clearToken();
      throw new Error("Not authenticated");
    }
  }

  // ✅ Store tokens securely
  // private storeTokens(tokens: { access: { token: string }; refresh: { token: string } }) {
  //   Cookies.set(ACCESS_TOKEN_KEY, tokens.access.token, {secure:true});
  //   Cookies.set(REFRESH_TOKEN_KEY, tokens.refresh.token, {secure:true,});
  // }

  // // Get access token
  // private getAccessToken(): string | undefined {
  //   return Cookies.get(ACCESS_TOKEN_KEY);
  // }

  // // Get refresh token
  // private getRefreshToken(): string | undefined {
  //   return Cookies.get(REFRESH_TOKEN_KEY);
  // }

  // // Clear tokens
  // private clearTokens() {
  //   Cookies.remove(ACCESS_TOKEN_KEY);
  //   Cookies.remove(REFRESH_TOKEN_KEY);
  // }
}

export const authApi = new AuthApi();
